import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Field, Button } from "@/components/brand";

const METRICS = [["workouts", "Workouts"], ["minutes", "Minutes"], ["streak", "Day streak"]];
const ICONS = ["award", "zap", "sun", "target", "heart", "activity"];

export function ChallengeFormDialog({ open, onClose, onSubmit, loading, allowCategory = true }) {
  const cats = useQuery({ queryKey: ["categories"], queryFn: () => api("/categories"), enabled: open });
  const [f, setF] = useState({ title: "", description: "", metric: "workouts", goal: 10, starts_at: "", ends_at: "", category: "", badge_icon: "award", reward_points: 200 });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = () => onSubmit({
    title: f.title, description: f.description, metric: f.metric, goal: Number(f.goal),
    starts_at: new Date(f.starts_at).toISOString(), ends_at: new Date(f.ends_at).toISOString(),
    category: f.category || null, badge_icon: f.badge_icon, reward_points: Number(f.reward_points),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg p-0 rounded-xl2 overflow-hidden" data-testid="challenge-form-dialog">
        <div className="p-5 space-y-3 max-h-[85vh] overflow-y-auto">
          <DialogTitle asChild><h2 className="font-display text-2xl text-ink">New Challenge</h2></DialogTitle>
          <Field label="Title" testID="challenge-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="30-Day Consistency" />
          <Field label="Description" as="textarea" testID="challenge-description" value={f.description} onChange={(e) => set("description", e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1.5"><span className="font-body font-semibold text-[13px] text-ink">Metric</span>
              <select data-testid="challenge-metric" value={f.metric} onChange={(e) => set("metric", e.target.value)} className="h-[52px] rounded-md border border-line bg-surface2 px-3 font-body text-[15px]">
                {METRICS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <Field label="Goal" testID="challenge-goal" type="number" value={f.goal} onChange={(e) => set("goal", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Starts" testID="challenge-starts" type="datetime-local" value={f.starts_at} onChange={(e) => set("starts_at", e.target.value)} />
            <Field label="Ends" testID="challenge-ends" type="datetime-local" value={f.ends_at} onChange={(e) => set("ends_at", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {allowCategory ? (
              <label className="flex flex-col gap-1.5"><span className="font-body font-semibold text-[13px] text-ink">Category (optional)</span>
                <select data-testid="challenge-category" value={f.category} onChange={(e) => set("category", e.target.value)} className="h-[52px] rounded-md border border-line bg-surface2 px-3 font-body text-[15px]">
                  <option value="">Any</option>
                  {(cats.data ?? []).map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
                </select>
              </label>
            ) : null}
            <label className="flex flex-col gap-1.5"><span className="font-body font-semibold text-[13px] text-ink">Badge icon</span>
              <select data-testid="challenge-icon" value={f.badge_icon} onChange={(e) => set("badge_icon", e.target.value)} className="h-[52px] rounded-md border border-line bg-surface2 px-3 font-body text-[15px]">
                {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </label>
          </div>
          <Field label="Reward points" testID="challenge-reward" type="number" value={f.reward_points} onChange={(e) => set("reward_points", e.target.value)} />
          <Button testID="challenge-create-submit" title="Create Challenge" loading={loading} disabled={!f.title || !f.starts_at || !f.ends_at} onClick={submit} full />
        </div>
      </DialogContent>
    </Dialog>
  );
}
